import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { useFinance, Field, inputCls, kicker, Loading, EmptyState, fmt, pct, todayISO } from "@/admin/finance/financeUtils";

function PnL() {
  const { data, loading } = useFinance("/finance/pnl", []);
  if (loading) return <Loading />;
  if (!data || !data.has_data) return <EmptyState label="No P&L data for this month yet." />;
  const row = (label, val, bold) => (
    <div className={`flex justify-between py-2 border-b border-[color:var(--border-subtle)]/40 ${bold ? "text-white font-semibold" : "text-secondary"}`}><span>{label}</span><span className="tabular-nums">{fmt(val)}</span></div>
  );
  const dc = data.direct_costs;
  return (
    <div className="ascendia-card p-5" data-testid="finance-pnl">
      <p className={kicker}>Monthly P&L ({data.period.from} → {data.period.to})</p>
      <div className="mt-3 text-sm">
        {row("Revenue", data.revenue, true)}
        {row("Labor", -dc.labor)}
        {row("Supplies", -dc.supplies)}
        {row("Fuel / travel", -dc.travel)}
        {row("= Gross profit", data.gross_profit, true)}
        {Object.entries(data.operating_expenses).map(([k, v]) => row(k, -v))}
        {data.refunds ? row("Refunds", -data.refunds) : null}
        {row("= Operating profit", data.operating_profit, true)}
      </div>
    </div>
  );
}

function ByService() {
  const { data, loading } = useFinance("/finance/by-service", []);
  if (loading) return <Loading />;
  if (!data || !data.length) return <EmptyState label="No completed jobs to compare yet." />;
  return (
    <div className="ascendia-card p-0 overflow-x-auto" data-testid="finance-by-service">
      <table className="w-full text-sm min-w-[640px]">
        <thead><tr className="text-left text-[11px] uppercase tracking-wide text-muted-c border-b border-[color:var(--border-subtle)]"><th className="p-3">Service</th><th className="p-3 text-right">Jobs</th><th className="p-3 text-right">Revenue</th><th className="p-3 text-right">Avg value</th><th className="p-3 text-right">Avg profit</th><th className="p-3 text-right">Margin</th></tr></thead>
        <tbody>
          {data.map((g) => (
            <tr key={g.service} className="border-b border-[color:var(--border-subtle)]/40"><td className="p-3 text-white">{g.service}</td><td className="p-3 text-right tabular-nums text-secondary">{g.jobs}</td><td className="p-3 text-right tabular-nums text-white">{fmt(g.revenue)}</td><td className="p-3 text-right tabular-nums text-secondary">{fmt(g.avg_job_value)}</td><td className="p-3 text-right tabular-nums text-secondary">{fmt(g.avg_profit)}</td><td className="p-3 text-right tabular-nums text-gold-soft">{pct(g.margin)}</td></tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CashFlow() {
  const { data, loading } = useFinance("/finance/cash-flow?group=month", []);
  if (loading) return <Loading />;
  if (!data || !data.rows.length) return <EmptyState label="No cash movement recorded yet." />;
  return (
    <div className="ascendia-card p-0 overflow-x-auto" data-testid="finance-cashflow">
      <table className="w-full text-sm min-w-[560px]">
        <thead><tr className="text-left text-[11px] uppercase tracking-wide text-muted-c border-b border-[color:var(--border-subtle)]"><th className="p-3">Month</th><th className="p-3 text-right">In</th><th className="p-3 text-right">Out</th><th className="p-3 text-right">Net</th><th className="p-3 text-right">Closing</th></tr></thead>
        <tbody>
          {data.rows.map((r) => (
            <tr key={r.period} className="border-b border-[color:var(--border-subtle)]/40"><td className="p-3 text-white">{r.period}</td><td className="p-3 text-right tabular-nums text-emerald-300">{fmt(r.in)}</td><td className="p-3 text-right tabular-nums text-rose-300">{fmt(r.out)}</td><td className="p-3 text-right tabular-nums text-white">{fmt(r.net)}</td><td className="p-3 text-right tabular-nums text-secondary">{fmt(r.closing)}</td></tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Forecast() {
  const { data, loading } = useFinance("/finance/forecast", []);
  if (loading) return <Loading />;
  if (!data || data.count === 0) return <EmptyState label="No confirmed upcoming jobs to forecast." />;
  return (
    <div className="ascendia-card p-5" data-testid="finance-forecast">
      <p className={kicker}>Forecast · confirmed upcoming ({data.count} jobs)</p>
      <div className="mt-3 grid grid-cols-3 gap-4">
        <div><p className="text-xs text-muted-c">Revenue</p><p className="text-white tabular-nums text-lg">{fmt(data.confirmed_revenue)}</p></div>
        <div><p className="text-xs text-muted-c">Expected cost</p><p className="text-white tabular-nums text-lg">{fmt(data.expected_cost)}</p></div>
        <div><p className="text-xs text-muted-c">Expected profit</p><p className="text-gold-soft tabular-nums text-lg">{fmt(data.expected_profit)}</p></div>
      </div>
    </div>
  );
}

function OwnerLedger() {
  const { data, loading, reload } = useFinance("/finance/owner-ledger", []);
  const [f, setF] = useState({ type: "withdrawal", amount: "", note: "", date: todayISO() });
  const add = async () => {
    if (!Number(f.amount)) { toast.error("Enter amount"); return; }
    try { await api.post("/finance/owner-ledger", { ...f, amount: Number(f.amount) }); toast.success("Saved"); setF({ ...f, amount: "", note: "" }); reload(); }
    catch (e) { console.error(e); toast.error("Could not save"); }
  };
  const del = async (id) => { try { await api.delete(`/finance/owner-ledger/${id}`); reload(); } catch (e) { console.error(e); } };
  return (
    <div className="ascendia-card p-5" data-testid="finance-owner-ledger">
      <p className={kicker}>Owner withdrawals / contributions</p>
      <p className="mt-1 text-[11px] text-muted-c">Kept separate from business expenses so profit stays accurate.</p>
      <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2 items-end">
        <Field label="Type"><select className={inputCls} value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })}><option value="withdrawal">Withdrawal</option><option value="contribution">Contribution</option><option value="reimbursement">Reimbursement</option></select></Field>
        <Field label="Amount"><input type="number" className={inputCls} value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} /></Field>
        <Field label="Note"><input className={inputCls} value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} /></Field>
        <button onClick={add} className="rounded-lg bg-gold py-2.5 text-sm font-semibold text-black hover:bg-gold-soft"><Plus className="inline h-4 w-4" /> Add</button>
      </div>
      <div className="mt-4 space-y-1">
        {(data || []).map((r) => (
          <div key={r.id} className="flex items-center justify-between text-sm border-b border-[color:var(--border-subtle)]/40 py-1.5"><span className="text-secondary">{r.date} · {r.type} {r.note ? `— ${r.note}` : ""}</span><span className="flex items-center gap-3"><span className="text-white tabular-nums">{fmt(r.amount)}</span><button onClick={() => del(r.id)} className="text-rose-300"><Trash2 className="h-3.5 w-3.5" /></button></span></div>
        ))}
        {loading && <Loading />}
        {data && data.length === 0 && <p className="text-xs text-muted-c">Nothing recorded.</p>}
      </div>
    </div>
  );
}

export default function FinanceReports() {
  const [view, setView] = useState("pnl");
  const VIEWS = [["pnl", "Monthly P&L"], ["service", "By service"], ["cashflow", "Cash flow"], ["forecast", "Forecast"], ["owner", "Owner"]];
  return (
    <div data-testid="finance-reports">
      <div className="flex gap-2 mb-4 flex-wrap">
        {VIEWS.map(([k, l]) => (
          <button key={k} onClick={() => setView(k)} data-testid={`report-${k}`} className={`rounded-lg px-3.5 py-2 text-sm border transition-colors ${view === k ? "bg-gold text-black border-gold" : "border-[color:var(--border-subtle)] text-white/80 hover:bg-white/5"}`}>{l}</button>
        ))}
      </div>
      {view === "pnl" && <PnL />}
      {view === "service" && <ByService />}
      {view === "cashflow" && <CashFlow />}
      {view === "forecast" && <Forecast />}
      {view === "owner" && <OwnerLedger />}
    </div>
  );
}
